import type { HrBadgeCounts } from "../types/hr.contract";

/** HR suite design tokens, shared by the `ui/` kit and every redesigned screen. */
export const DASH_INK = {
  primary: "#051912",
  secondary: "#3F4D48",
  muted: "#6B7A74",
  faint: "#9AA6A1",
} as const;

export const DASH_LINE = {
  hairline: "#E4EAE7",
  grid: "#EDF1EF",
} as const;

export const DASH_GREEN = {
  brand: "#218760",
  deep: "#1E5D43",
  ink: "#143D2D",
  mint: "#EAF5F0",
  mintStrong: "#D6EBE3",
} as const;

/** Every motion on the dashboard uses this curve: a heavy, settled ease-out. */
export const DASH_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

/**
 * Today's composition, in bar order. Validated with the dataviz palette
 * checker on the card surface (#f8fbf9): every adjacent pair clears the CVD
 * and normal-vision floors. Amber sits under 3:1 contrast, so every segment
 * also carries a visible label and count in the legend.
 *
 * LATE and EARLY_EXIT are flags on people who ARE in, so they fold into
 * "In office" here (the KPI tiles count them separately). Seven hues cannot
 * clear the floors; five can.
 */
export type TodaySegmentKey = "IN_OFFICE" | "WFH" | "HALF_DAY" | "LEAVE" | "ABSENT" | "NOT_IN_YET";

export const TODAY_SEGMENTS: readonly { key: TodaySegmentKey; label: string; color: string }[] = [
  { key: "IN_OFFICE", label: "In office", color: "#218760" },
  { key: "WFH", label: "From home", color: "#2A78D6" },
  { key: "HALF_DAY", label: "Half day", color: "#EDA100" },
  { key: "LEAVE", label: "On leave", color: "#6250D6" },
  { key: "ABSENT", label: "Absent", color: "#E34948" },
  // Neutral, not a category: "nothing recorded yet" reads as absence of data.
  { key: "NOT_IN_YET", label: "Not in yet", color: "#C9D1CD" },
];

/**
 * Fold the server's per-badge counts into the five segments plus "not in
 * yet". `notInYet` people still carry the ABSENT badge until their shift is
 * over, so they are taken out of Absent rather than counted twice.
 */
export function todaySegments(counts: HrBadgeCounts, notInYet: number) {
  const absent = Math.max(0, counts.ABSENT - notInYet);
  const values: Record<TodaySegmentKey, number> = {
    IN_OFFICE: counts.PRESENT + counts.LATE + counts.EARLY_EXIT,
    WFH: counts.WFH,
    HALF_DAY: counts.HALF_DAY,
    LEAVE: counts.LEAVE,
    ABSENT: absent,
    NOT_IN_YET: Math.min(notInYet, counts.ABSENT),
  };
  return TODAY_SEGMENTS.map((s) => ({ ...s, value: values[s.key] }));
}

/** Single-hue ramp for magnitude (departments, headcount, arrivals). */
export const DASH_SERIES = DASH_GREEN.brand;
